import React, { useState, useEffect, useContext } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Title from '../components/Title';
import SubmitButton from '../components/SubmitButton';
import useGtmEventTracking from '../components/useGtmEventTracking';
import UserContext from '../contexts/UserContext';
import { useFlow } from '../utilities/useFlow';

interface LandingProps extends RouteComponentProps {}

function LandingComponent({ history }: LandingProps): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const gtmEventTracking = useGtmEventTracking();
  const flow = useFlow();
  const isKeepr = flow === 'Keepr';

  useEffect(() => {
    gtmEventTracking({ event: 'homepage loaded', value: true });
  }, [gtmEventTracking]);

  const active = 'rollover';
  const nonActive = 'those-freaking-boxes';
  const onMouseLeave = () => setHover(null);

  const { user } = useContext(UserContext);

  function welcomeMessage(): JSX.Element {
    let welcomeText: string;
    if (user) {
      welcomeText = 'Welcome back, ' + user.firstName + '. ';
      return (
        <div>
          <span>
            {welcomeText} <a href="/dashboard">Manage your account</a>
          </span>
        </div>
      );
    } else {
      welcomeText = 'Returning users please login';
      return (
        <div>
          <Link
            onClick={() => {
              gtmEventTracking({ event: 'login_link' });
            }}
            to="/login"
          >
            Login
          </Link>
          <span>
            <br />
            <a href="/register">Register</a>
          </span>
        </div>
      );
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      <Helmet>
        <title>{`Student Storage & Removals | ${flow}`}</title>
        <meta
          name="description"
          content={`The #1 Student Storage and Shipping service in the UK. Door-to-door box storage and removals from ${flow}.`}
        />
      </Helmet>
      <section className="login-status-bar">
        <div className="status-bar-content">
          <p className="grey"> {welcomeMessage()}</p>
        </div>
      </section>
      <Title>Let's get you moving...</Title>
      <p className="grey">
        {flow} are experts at storing and shipping boxes all across the UK.
      </p>
      <div className="landing-all-box-container">
        <LandingBox
          title="Store Boxes"
          priceTag=""
          description="We provide an all-inclusive service including boxes, packing materials, collection, storage and delivery (one month minimum storage charge)."
          buttonText="Book Storage"
          className={hover === 1 ? active : nonActive}
          onClick={() => {
            history.push('/storage/boxes');
            gtmEventTracking({ event: 'booking_step_option_storage' });
          }}
          staticStateImg="/img/box-icon-large.png"
          rolloverStateImg="/img/box-icon-white-large.png"
          onMouseEnter={() => setHover(1)}
          onMouseLeave={onMouseLeave}
        />
        <LandingBox
          title="Ship Boxes"
          priceTag=""
          description="Our shipping service includes empty boxes, packing materials, collection and delivery of your items to anywhere in the UK."
          buttonText="Ship Boxes"
          className={hover === 2 ? active : nonActive}
          onClick={() => {
            history.push('/shipping/boxes');
            gtmEventTracking({ event: 'booking_step_option_ship' });
          }}
          staticStateImg="/img/ship-icon.png"
          rolloverStateImg="/img/ship-icon-white.png"
          onMouseEnter={() => setHover(2)}
          onMouseLeave={onMouseLeave}
        />
        {isKeepr && (
          <LandingBox
            title="Removals"
            priceTag=""
            description="Our professional removals service includes collection, transportation and delivery of your furniture and belongings."
            buttonText="Book Removal"
            className={hover === 3 ? active : nonActive}
            onClick={() => {
              history.push('/removals');
              gtmEventTracking({ event: 'booking_step_option_removals' });
            }}
            staticStateImg="/img/truck.png"
            rolloverStateImg="/img/truck.png"
            onMouseEnter={() => setHover(3)}
            onMouseLeave={onMouseLeave}
          />
        )}
      </div>
    </div>
  );
}

interface LandingBoxProps {
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  title: string;
  priceTag: string;
  description: string;
  buttonText: string;
  className: string;
  staticStateImg: string;
  rolloverStateImg: string;
}

function LandingBox({
  description,
  priceTag,
  title,
  buttonText,
  staticStateImg,
  rolloverStateImg,
  ...props
}: LandingBoxProps) {
  return (
    <section {...props}>
      <div className="content">
        <h3>{title}</h3>
        <img className="static-state" src={staticStateImg} alt="background" />
        <img
          className="rollover-state"
          src={rolloverStateImg}
          alt="highlighted background"
        />
        <div className="jessieJ-priceTag">{priceTag}</div>
        <p>{description}</p>
      </div>
      <SubmitButton>{buttonText}</SubmitButton>
    </section>
  );
}

export default withRouter(LandingComponent);
